/**
 * Single date+time field — same react-minimal-datetime-range UX as GreenTimeRangePicker
 * (calendar → Select time → Confirm), scoped to one datetime instead of a range.
 */
import React, { useState } from "react";
import { Box, IconButton, Popover } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { styled } from "@mui/material/styles";
import { GreenTextField } from "../greenTextField";
import colors from "../../../utils/colors";
import SingleDateTimePickerPanel from "./SingleDateTimePickerPanel";
import { layerdPickerPopoverPaperSx } from "./styles";
import { formatDisplayValue } from "./utils";

const SingleDateTimeTextField = styled(GreenTextField, {
  shouldForwardProp: (prop) => prop !== "hasClearValue",
})(({ hasClearValue }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    width: "100%",
    minHeight: 48,
    cursor: "pointer",
    boxSizing: "border-box",
    "& .MuiOutlinedInput-input": {
      padding: `22px ${hasClearValue ? 36 : 16}px 6px 16px !important`,
      fontSize: 14,
      lineHeight: "14px",
      color: colors.neutral[1000],
      cursor: "pointer",
      boxSizing: "border-box",
      "&::placeholder": {
        fontSize: 14,
        lineHeight: "14px",
        color: colors.neutral[500],
        opacity: 1,
      },
    },
  },
}));

export default function GreenSingleDateTimeField({
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  fullWidth,
  sx,
  InputLabelProps,
  placeholder = "Select date & time",
  disablePast = false,
  ...rest
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const closePicker = () => {
    setAnchorEl(null);
    onBlur?.();
  };

  const handleConfirm = (next) => {
    onChange?.({ target: { value: next } });
    closePicker();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.({ target: { value: "" } });
  };

  return (
    <>
      <Box sx={{ position: "relative", width: "100%" }}>
        <SingleDateTimeTextField
          {...rest}
          fullWidth={fullWidth}
          hasClearValue={Boolean(value)}
          label={label}
          value={formatDisplayValue(value)}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true, ...InputLabelProps }}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          inputProps={{ readOnly: true }}
          sx={sx}
        />
        {value ? (
          <IconButton
            size="small"
            onClick={handleClear}
            aria-label="Clear date and time"
            sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", p: 0.5 }}
          >
            <ClearIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
          </IconButton>
        ) : null}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: layerdPickerPopoverPaperSx } }}
      >
        <SingleDateTimePickerPanel
          key={`${open}|${value}`}
          value={value}
          disablePast={disablePast}
          onConfirm={handleConfirm}
        />
      </Popover>
    </>
  );
}
